<script setup lang="ts">
import { type Ref, ref, watch } from 'vue'
import type { DrawerConfig } from '../../helpers/drawer'
import FormSection from '../LeadForm/parts/FormSection.vue'
import { EDrawerType } from './types'
import DrawerTextBlock from './parts/DrawerTextBlock.vue'
import DrawerImageBlock from './parts/DrawerImageBlock.vue'
import DrawerInfobox from './parts/DrawerInfobox.vue'
import DrawerAccordion from './parts/DrawerAccordion.vue'
import DrawerLinkList from './parts/DrawerLinkList.vue'
import DrawerButtonBlock from './parts/DrawerButtonBlock.vue'
import DrawerHtmlContainer from './parts/DrawerHtmlContainer.vue'
import DrawerTextAndImageBlock from './parts/DrawerTextAndImageBlock.vue'
import VDrawer from '@/ui/plushComponents/VDrawer/VDrawer.vue'
import { isDrawerOpen, useDrawer } from '@/cms-ui/composables/useDrawer'

const components = {
    DrawerTextBlock,
    DrawerImageBlock,
    DrawerInfobox,
    DrawerAccordion,
    DrawerLinkList,
    DrawerButtonBlock,
    DrawerHtmlContainer,
    DrawerTextAndImageBlock,
    LeadForm: FormSection,
} as const

type ContentItem = keyof typeof components

const currentDrawer: Ref<DrawerConfig | undefined> = ref(undefined)
const { drawers } = useDrawer()

watch(
    drawers,
    drawers => {
        if (drawers.length) {
            currentDrawer.value = drawers.at(0)
            isDrawerOpen.value = true
        }
    },
    { deep: true, immediate: true },
)

watch(isDrawerOpen, isOpen => {
    if (!isOpen) {
        currentDrawer.value?.close?.()
    }
})
</script>

<template>
    <ClientOnly>
        <VDrawer
            v-model="isDrawerOpen"
            data-test="as"
            :full-height="currentDrawer?.data.drawerTypeMobile === EDrawerType.FULL_HEIGHT"
            :title="currentDrawer?.data.drawerTitle"
            :description="currentDrawer?.data.drawerDescription"
        >
            <div class="pt-2xl md:pt-[80px]">
                <template
                    v-for="(item, index) in currentDrawer?.data.content"
                    :key="`Drawer_content_${index}`"
                >
                    <component
                        :is="components[item.type as ContentItem]"
                        v-bind="{ ...item.data }"
                        class="first-of-type:-mt-base lg:mt-lg mt-6"
                    />
                </template>
            </div>
        </VDrawer>
    </ClientOnly>
</template>
